class CartDrawer extends HTMLElement {
  
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    
    // Add listener for cart updates
    document.addEventListener('cart:refresh', () => {
      this.refreshCart();
    });
  }

  async refreshCart() {
    try {
      // First, get the current cart state to compare
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      
      const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      
      // Get the entire drawer inner content
      const drawerInner = this.querySelector('.drawer__inner');
      
      // Better way to check if cart has items - look for table rows or check cart item count
      const hasItems = html.querySelector('cart-drawer-items tbody tr') || 
                      html.querySelector('.cart-item') ||
                      html.querySelector('[data-cart-item]') ||
                      html.querySelector('form#CartDrawer-Form');
      
      // Also check if the new content has the is-empty class
      const newCartDrawer = html.querySelector('cart-drawer');
      const newIsEmpty = newCartDrawer ? newCartDrawer.classList.contains('is-empty') : true;
      
      // Additional check: look for the empty text vs form content
      const hasEmptyText = html.querySelector('.cart__empty-text');
      const hasForm = html.querySelector('form#CartDrawer-Form');
      
      // Force non-empty if we have a form OR if the cart data shows items
      const shouldShowItems = hasForm || (hasItems && !newIsEmpty) || cartData.item_count > 0;
      
      if (shouldShowItems) {
        // If we have items, remove empty state and update content
        this.classList.remove('is-empty');
        
        // If the section response doesn't match the cart data, refetch
        if (cartData.item_count > 0 && (!hasForm && !hasItems)) {
          // Wait a bit and retry
          setTimeout(() => this.refreshCart(), 500);
          return;
        }
        
        const newDrawerInner = html.querySelector('.drawer__inner');
        if (newDrawerInner) {
          drawerInner.innerHTML = newDrawerInner.innerHTML;
        } else {
          drawerInner.innerHTML = html.querySelector('.drawer__inner').innerHTML;
        }
        
        // Initialize cart drawer items if needed
        const cartDrawerItems = drawerInner.querySelector('cart-drawer-items');
        if (cartDrawerItems && !cartDrawerItems.initialized) {
          cartDrawerItems.initialized = true;
          customElements.upgrade(cartDrawerItems);
        }
      } else {
        // If empty, add empty state and show empty message
        this.classList.add('is-empty');
        // Update with the actual empty content from the server
        const newDrawerInner = html.querySelector('.drawer__inner');
        if (newDrawerInner) {
          drawerInner.innerHTML = newDrawerInner.innerHTML;
        } else {
          drawerInner.innerHTML = '<cart-drawer-items><h2 class="cart__empty-text">Your cart is empty</h2></cart-drawer-items>';
        }
      }
      
      // Reattach event listeners
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        // Remove existing listeners to avoid duplicates
        overlay.removeEventListener('click', this.close.bind(this));
        overlay.addEventListener('click', this.close.bind(this));
      }
      
      // Open drawer if not already open
      if (!this.classList.contains('active')) {
        this.open();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Use requestAnimationFrame for smoother animation
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState.sections) {
      return;
    }

    // Remove empty state immediately when items are added
    if (parsedState.item_count > 0) {
      this.classList.remove('is-empty');
      
      // First update the inner content with the new sections
      if (parsedState.sections['cart-drawer']) {
        const html = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
        const drawerInner = this.querySelector('.drawer__inner');
        
        if (drawerInner) {
          // Replace the entire inner content
          drawerInner.innerHTML = html.querySelector('.drawer__inner').innerHTML;
          
          // Initialize cart drawer items if needed
          const cartDrawerItems = drawerInner.querySelector('cart-drawer-items');
          if (cartDrawerItems && !cartDrawerItems.initialized) {
            cartDrawerItems.initialized = true;
            customElements.upgrade(cartDrawerItems);
          }
          
          // Reattach event listeners
          const overlay = this.querySelector('#CartDrawer-Overlay');
          if (overlay) {
            overlay.removeEventListener('click', this.close.bind(this));
            overlay.addEventListener('click', this.close.bind(this));
          }
        }
      }
      
      // Update cart icon bubble if available
      if (parsedState.sections['cart-icon-bubble']) {
        const cartIcon = document.querySelector('#cart-icon-bubble');
        if (cartIcon) {
          const iconHtml = new DOMParser().parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html');
          const newIconContent = iconHtml.querySelector('.shopify-section');
          if (newIconContent) {
            cartIcon.innerHTML = newIconContent.innerHTML;
          }
        }
      }
      
      // Then open drawer
      this.open();
    } else {
      // Handle empty cart state
      this.classList.add('is-empty');
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) {
        // Show empty state
        drawerInner.innerHTML = '<cart-drawer-items><h2 class="cart__empty-text">Your cart is empty</h2></cart-drawer-items>';
      }
    }
    
    // Always dispatch refresh event at the end
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.initialized = true;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}
